import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  CardElement,
  useStripe,
  useElements,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import compose from 'lodash.flowright';
import { useQuery } from 'react-apollo';
import Select from 'react-select';

import { mapProps } from '../../../util/hocs';
import { getErrorString } from '../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../util/sentry';
import FormGroup from '../FormGroup';
import Alert from '../Alert';
import Button from '../Button';
import withModal from '../modal/withModal';
import PromoCodeInput from '../PromoCodeInput/PromoCodeInput';
import { checkoutPrice as checkoutPriceQuery } from '../../../graphql/queries/checkout';
import usePaymentRequestButton from './usePaymentRequestButton';
import useCheckoutAccountName from './useCheckoutAccountName';
import PriceDisplay from '../PriceDisplay';
import { MeForCheckout } from '../../../graphql/queries/account';
import Spinner from '../Spinner';
import BoxedCustomControl from '../BoxedCustomControl';
import CreditCardIcon from '../../icons/CreditCardIcon';

export const STRIPE_CHECKOUT_MODAL = 'modal/STRIPE_CHECKOUT';

const propTypes = {
  className: PropTypes.string,
  onSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  description: PropTypes.node,
  buttonLabel: PropTypes.node.isRequired,
  accountReferralTypes: PropTypes.arrayOf(PropTypes.string),
  checkoutPriceVariables: PropTypes.func,
  price: PropTypes.shape({
    value: PropTypes.number.isRequired,
    currency: PropTypes.string,
  }),
  cardRequired: PropTypes.bool,
  defaultName: PropTypes.string,
  defaultEmail: PropTypes.string,
  defaultPromoCode: PropTypes.string,
  setStripeConnectAccountId: PropTypes.func.isRequired,
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#646e71',
      color: '#646e71',
      fontFamily: 'Muli, sans-serif',
      fontSize: '1rem',
      fontSmoothing: 'antialiased',
      fontWeight: 'normal',
      backgroundColor: '#ffffff',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#646e71',
        fontWeight: 'normal',
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const StripeCheckoutModal = ({
  className,
  onSuccess,
  onClose,
  title = <h3 className="text-center">Checkout</h3>,
  description,
  buttonLabel,
  freeButtonLabel = 'FREE',
  accountReferralTypes = [],
  checkoutPriceVariables,
  price,
  cardRequired = true,
  defaultName = '',
  defaultEmail = '',
  defaultPromoCode = '',
  setStripeConnectAccountId,
}) => {
  const classes = classNames({
    'stripe-checkout-form-wrap': true,
    [className]: !!className,
  });

  const [name, setName] = useState(
    defaultName.toLowerCase() !== 'me' ? defaultName : '',
  );
  const [email, setEmail] = useState(defaultEmail);
  const [promoCode, setPromoCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [responseError, setResponseError] = useState('');
  const checkPromoCodeRef = useRef();
  const [shouldCheckPromoCode, setShouldCheckPromoCode] = useState(false);
  const checkoutPriceVariablesResult = checkoutPriceVariables?.({
    promo_code: promoCode,
  });
  const { data: { checkoutPrice } = {}, loading: loadingPricing } = useQuery(
    checkoutPriceQuery,
    {
      variables: checkoutPriceVariablesResult,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !checkoutPriceVariables || !checkoutPriceVariablesResult,
    },
  );
  const { data: { me: account } = {}, loading: loadingAccount } = useQuery(
    MeForCheckout,
    {
      fetchPolicy: 'network-only',
    },
  );

  const stripeConnectAccountId = checkoutPrice?.stripe_connect_account_id;

  const { saveCheckoutAccountName } = useCheckoutAccountName({ account });

  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const finalAmount = checkoutPrice?.total_price?.value ?? price?.value;
  const isCardFreeCheckout = !finalAmount && !cardRequired;

  const stripe = useStripe();
  const elements = useElements();

  const { paymentRequest } = usePaymentRequestButton({
    amount: finalAmount,
    currency: checkoutPrice?.total_price?.currency?.toLowerCase(),
    onStripeToken: async ({ token }) => {
      onClose();
      await saveCheckoutAccountName(name || token?.card?.name);
      await onSuccess({
        ...token,
        email: email || token.email,
        promo_code: promoCode,
        stripe_connect_account_id: stripeConnectAccountId,
      });
    },
  });

  const handleSubmit = async event => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);
    setResponseError('');

    try {
      if (shouldCheckPromoCode && checkPromoCodeRef.current) {
        const success = await checkPromoCodeRef.current();

        if (!success) {
          setSubmitting(false);
          setResponseError('');
          return;
        }
      }
      if (isCardFreeCheckout) {
        // Free checkout:
        await saveCheckoutAccountName(name);
        onSuccess?.({
          email,
          promo_code: promoCode,
          stripe_connect_account_id: stripeConnectAccountId,
        });

        onClose();
        return;
      }

      const { error, token } = paymentMethodId
        ? {
            token: {
              stripe_payment_method_id: paymentMethodId,
            },
          }
        : await stripe.createToken(elements.getElement(CardElement), {
            name,
          });

      if (error) {
        throw Error(error.message);
      }

      await saveCheckoutAccountName(name);
      setSubmitting(false);
      onSuccess?.({
        ...token,
        email,
        promo_code: promoCode,
        stripe_connect_account_id: stripeConnectAccountId,
      });
      onClose();
    } catch (error) {
      setResponseError(getErrorString(error));
      setSubmitting(false);

      sentryCaptureMessage('Checkout error ', {
        level: 'error',
        extra: {
          error,
          email,
        },
      });
    }
  };

  useEffect(() => {
    if (
      account?.default_payment_method &&
      !isCardFreeCheckout &&
      !stripeConnectAccountId
    ) {
      setPaymentMethodId(account.default_payment_method.id);
    }
  }, [account, isCardFreeCheckout]);

  useEffect(() => {
    if (stripeConnectAccountId) {
      setPaymentMethodId(null);
    }

    setStripeConnectAccountId(stripeConnectAccountId);
  }, [stripeConnectAccountId]);

  if (loadingAccount) {
    return (
      <div className={classes}>
        <Spinner className="m-auto d-flex" />
      </div>
    );
  }

  const paymentMethods = account?.payment_methods ?? [];

  const userForm = (
    <fieldset className="stripe-checkout-form-fieldset">
      <FormGroup
        label="Name"
        labelSrOnly
        id="card-name"
        name="name"
        placeholder={isCardFreeCheckout ? 'Your name' : 'Name on card'}
        required
        value={name}
        onChange={event => {
          const { target } = event;
          const { value } = target;
          setName(value);
        }}
      />
      <FormGroup
        label="Email"
        labelSrOnly
        type="email"
        id="card-email"
        name="email"
        placeholder="Email"
        required
        value={email}
        pattern=".*@.*\..*"
        title="email@example.com"
        onChange={event => {
          const { target } = event;
          const { value } = target;
          setEmail(value);
        }}
      />
    </fieldset>
  );

  return (
    <div className={classes}>
      {title}
      {description}

      <form onSubmit={handleSubmit} className="stripe-checkout-form">
        {!isCardFreeCheckout && (
          <>
            {!stripeConnectAccountId && paymentMethods.length > 0 && (
              <Select
                className="stripe-checkout-form-payment-method-selection"
                classNamePrefix="stripe-checkout-form-payment-method-selection"
                inputId="payment-methods"
                placeholder="Select an existing card"
                value={
                  paymentMethodId
                    ? paymentMethods.find(
                        paymentMethod => paymentMethod.id === paymentMethodId,
                      )
                    : null
                }
                noOptionsMessage="Select an existing payment method"
                options={paymentMethods}
                getOptionValue={paymentMethod => paymentMethod.id}
                getOptionLabel={paymentMethod => (
                  <div key={paymentMethod.id}>
                    <div className="d-flex align-items-center mb-1 text-dark">
                      <CreditCardIcon className="mr-1" color="#1a2e34" />
                      <span className="text-bold">
                        {Array(12)
                          .fill()
                          .map((_, n) => (
                            <>&#9679;{(n + 1) % 4 === 0 && <> </>}</>
                          ))}{' '}
                        {paymentMethod.last_4}
                      </span>
                    </div>
                    <div className="text-dark">
                      {paymentMethod.brand} Expires at{' '}
                      {paymentMethod.expiry_month}/{paymentMethod.expiry_year}
                    </div>
                  </div>
                )}
                onChange={paymentMethod => {
                  setPaymentMethodId(paymentMethod.id);
                }}
              />
            )}
            <BoxedCustomControl
              id="add-new-card"
              type="radio"
              onChange={() => {
                setPaymentMethodId(null);
              }}
              checked={!paymentMethodId}
              className="stripe-checkout-form-payment-method"
              label="Add new card"
            >
              {(stripeConnectAccountId || !paymentMethodId) && (
                <>
                  {userForm}
                  {!isCardFreeCheckout && (
                    <fieldset className="stripe-checkout-form-fieldset stripe-checkout-form-card-fieldset">
                      <CardElement options={CARD_ELEMENT_OPTIONS} />
                    </fieldset>
                  )}
                </>
              )}
            </BoxedCustomControl>
          </>
        )}
        {isCardFreeCheckout && (
          <div className="stripe-checkout-form-fieldset-styled">{userForm}</div>
        )}
        {accountReferralTypes.length > 0 && (
          <fieldset className="stripe-checkout-form-fieldset stripe-checkout-form-promo-code-fieldset">
            <PromoCodeInput
              defaultPromoCode={defaultPromoCode}
              accountReferralTypes={accountReferralTypes}
              labelSrOnly
              placeholder="Have a Promo Code?"
              onPromoCodeChange={setPromoCode}
              onPromoCodeUpdate={(newPromoCode, isApplied, checkPromoCode) => {
                if (newPromoCode && !isApplied) {
                  setShouldCheckPromoCode(true);
                } else {
                  setShouldCheckPromoCode(false);
                }
                checkPromoCodeRef.current = checkPromoCode;
              }}
              eventAddonItemIds={(
                checkoutPriceVariablesResult?.event_addon_items ?? []
              ).map(item => item.event_addon_item_id)}
            />
          </fieldset>
        )}
        {responseError && (
          <Alert type="danger">
            <strong>Error:</strong> {responseError}
          </Alert>
        )}
        <div className="text-center mt-3">
          <Button
            type="submit"
            btnSize="lg"
            disabled={submitting}
            btnStyle="yellow"
          >
            {}
            {(() => {
              if (submitting || loadingPricing) {
                return 'Processing...';
              }

              if (checkoutPrice?.total_price) {
                return checkoutPrice?.total_price?.value ? (
                  <>
                    <span className="mr-1">Pay</span>
                    <PriceDisplay
                      price={checkoutPrice?.total_price?.value ?? 0}
                      currency={checkoutPrice?.total_price?.currency}
                      oldPrice={
                        checkoutPrice?.total_price?.value +
                        checkoutPrice?.total_discount_price?.value
                      }
                      showDiscount
                      animatedStrikethrough
                      trimDecimalZeros={false}
                      loading={loadingPricing}
                    />
                  </>
                ) : (
                  freeButtonLabel
                );
              }

              return buttonLabel;
            })()}
          </Button>
        </div>
      </form>

      {/* A wallet sheet cannot charge $0 and would keep the pre-discount
          total, so it is hidden once a promo code makes the order free. */}
      {paymentRequest && !isCardFreeCheckout && (
        <fieldset className="stripe-checkout-form-fieldset stripe-checkout-form-payment-button mt-4">
          <span className="stripe-checkout-form-fieldset-label">
            Or check out with one click:
          </span>
          <PaymentRequestButtonElement options={{ paymentRequest }} />
        </fieldset>
      )}

      <div className="text-center">
        <Button
          type="button"
          btnStyle="link"
          className="mt-2"
          onClick={onClose}
          btnBlock
          disabled={submitting}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
};

StripeCheckoutModal.propTypes = propTypes;

export default withModal(
  compose(
    mapProps(({ onRequestClose, actionProps = {}, ...props }) => ({
      onClose: onRequestClose,
      ...actionProps,
      ...props,
    })),
  )(StripeCheckoutModal),
  {
    name: STRIPE_CHECKOUT_MODAL,
    size: 'sm',
    type: 'full',
    contentLabel: '',
    hideHeader: true,
    className: 'stripe-checkout-modal',
    overlayClassName: 'stripe-checkout-modal-overlay z-modal-max',
  },
);
